import { useRef, useState } from "react";
import CustomPopup from "../Popups/CustomPopup.js";
import Filters from "./Toolbar/Filters.tsx";
import Image from "components/Elements/Image/Image.tsx";
import LabelAs from "../Popups/LabelAs.tsx";
import PaginationComponent from "../Reusable/PaginationComponent.js";
import StatusFilterBar from "../Reusable/StatusFilterBar.tsx";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import useInboxQueryParams from "hooks/useInboxQueryParams.ts";
import useMediaQueries from "hooks/useMediaQueries.js";
import useOnClickOutside from "hooks/useOnClickOutside.js";
import useRemoveMailFunction from "hooks/useRemoveMailFunction.js";
import LoadingImageElement from "components/Elements/LoadingContainerElement/LoadingImageElement.tsx";
import Icon, { IconWithTooltip } from "components/Elements/Icon/Icon.tsx";
import { LinkToNewTab } from "components/Elements/Link/Link.js";
import { selectOptionsList } from "../helpers.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import useInbox from "hooks/useInbox.ts";
import { usePicker } from "contexts/pickerContext/pickerContext.ts";
import { useUser } from "contexts/userContext/userContext.js";
import { useToasts } from "react-toast-notifications";
import { useAppSelector } from "store/hooks.ts";
import { selectTParam } from "store/selectors/inboxSelectors.ts";

const SelectedComponentsActions = () => {
  const {
    getSelectedList,
    getSearchParams,
    setMailDataProp,
    getMailsData,
    removeMailForever,
    setMailIsSaved,
    setMailStatus,
  } = useInbox();
  const pageType = useAppSelector(selectTParam);
  const [showLabelAs, setShowLabelAs] = useState(false);
  const { isSubscribed, getUserData } = useUser();
  const { getTextByKey } = useContent();
  const { addToast } = useToasts();

  const [removeLoading, setRemoveLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const getSavedMailsList = () => {
    return getSelectedList().filter(
      (item) =>
        getMailsData()[item].mailStatus === "saved" ||
        getMailsData()[item].isSaved
    );
  };

  // list of mails that can be saved
  const getActiveMailsList = () => {
    return getSelectedList().filter((item) =>
      [
        "active",
        "downloaded",
        "orderCreated",
        "sending",
        "sent",
        "uploading",
      ].includes(getMailsData()[item]?.mailStatus)
    );
  };

  // const areAllSelectedRemoved = () => {
  //   return getSelectedList().every((item) =>
  //     ["removed", "uploadingError", "error"].includes(
  //       getMailsData()[item]?.mailStatus
  //     )
  //   );
  // };

  const areSomeSelectedRemoved = () => {
    return getSelectedList().some((item) =>
      ["removed", "uploadingError", "error"].includes(
        getMailsData()[item]?.mailStatus
      )
    );
  };

  const getActiveArray = () => {
    return getSelectedList().filter((mailId) => {
      return [
        "active",
        "downloaded",
        "orderCreated",
        "sending",
        "sent",
        "uploading",
      ].includes(getMailsData()[mailId]?.mailStatus);
    });
  };

  const checkAllMailsCanBeRemovedForever = () => {
    return getSelectedList().every((mailId) => {
      return (
        getMailsData()[mailId]?.deletedUserFullName ===
        `${getUserData()?.UserFirstName} ${getUserData()?.UserLastName}`
      );
    });
  };

  const removeMailsForever = async () => {
    const response = await removeMailForever({
      encryptedMailIds: getSelectedList(),
      setLoadingStatus: setRemoveLoading,
    });

    if (response?.status === "success") {
      addToast(getTextByKey("mail-removed-forever"), {
        appearance: "success",
      });
    } else {
      addToast(getTextByKey("mail-not-removed-forever"), {
        appearance: "error",
      });
    }
  };

  const getPendingRemovedArray = () =>
    getSelectedList().filter((mailId) => {
      return getMailsData()[mailId]?.mailStatus === "pendingRemoved";
    });

  const handleSaveRequest = async () => {
    if (!isSubscribed()) {
      return;
    }
    setSaveLoading(true);
    const mailsListForReq =
      getActiveMailsList().length > 0
        ? getActiveMailsList()
        : getSavedMailsList();

    const mailsUnSaveStatusReq = getActiveMailsList().length > 0 ? false : true;

    const params = {
      MailIdList: mailsListForReq,
      IsUnsave: mailsUnSaveStatusReq,
      isCollection: false,
    };

    const response = await apiRequest(
      "mails",
      "ReactSaveMultipleMailAsSubscriber",
      params,
      "POST"
    );

    if (response.status === "success") {
      mailsListForReq.forEach((mailId, i) => {
        if (!getMailsData()[mailId].deliveryNumber) {
          !mailsUnSaveStatusReq
            ? setMailStatus(mailId, "saved")
            : setMailDataProp(
                mailId,
                ["mailStatus", "expirationDate"],
                ["active", response.data[i]]
              );
        } else {
          setMailIsSaved(mailId, !mailsUnSaveStatusReq);
        }
      });
    }
    setSaveLoading(false);
    return response;
  };

  // remove files from mails
  const handleRemoveSelect = useRemoveMailFunction({
    activeArray: getSelectedList(),
    pendingArray: getPendingRemovedArray(),
    setLoading: setRemoveLoading,
  });

  const handleOpenLabelAs = () => {
    setShowLabelAs(true);
  };

  return (
    <>
      <div className="select-list-counter">
        {getSelectedList().length +
          " " +
          getTextByKey("selectionheader-selected")}
      </div>
      <div className="select-list-actions">
        {(["sent", "all"].includes(pageType) ||
          ["jform"].includes(getSearchParams().f)) &&
        getActiveArray().length > 0 ? (
          <>
            {isSubscribed() ? (
              <SaveIconButton
                {...{ saveLoading, handleSaveRequest, getActiveMailsList }}
              />
            ) : (
              <LinkToNewTab page="plans" isNewTab>
                <SaveIconButton
                  {...{ saveLoading, handleSaveRequest, getActiveMailsList }}
                />
              </LinkToNewTab>
            )}
          </>
        ) : null}
        {(["sent", "all", "trash"].includes(pageType) ||
          ["jform"].includes(getSearchParams().f)) &&
          // If it is trash, show "remove forever" icon
          // only if all selected mails can be removed forever.
          (pageType !== "trash" || checkAllMailsCanBeRemovedForever()) && (
            <Tooltip
              id={"multipleRemoveAction"}
              tooltipTextKey={
                pageType === "trash"
                  ? "remove-mail-forever-tooltip"
                  : "multiple-remove-action-tooltip-text"
              }
              bottom
            >
              {removeLoading ? (
                <Image
                  className="loader-animation-gif"
                  name="preloader"
                  ext="gif"
                  alt=""
                  title=""
                />
              ) : (
                <Icon
                  name="remove"
                  gray
                  onClick={
                    pageType === "trash"
                      ? removeMailsForever
                      : handleRemoveSelect
                  }
                />
              )}
            </Tooltip>
          )}
        {/* {(["sent"].includes(pageType) ||
          ["jform"].includes(getSearchParams().f)) &&
        areSomeSelectedRemoved() ? (
          <Tooltip
            id={"multipleRemoveCompletelyAction"}
            tooltipTextKey={"multiple-remove-completely-action-tooltip-text"}
            bottom
          >
            {isPendingRemovalToTrash ? (
              <Image
                className="loader-animation-gif"
                name="preloader"
                ext="gif"
                alt=""
                title=""
              />
            ) : (
              <Icon name={"remove"} gray onClick={removeMailsToTrash} />
            )}
          </Tooltip>
        ) : null} */}
        <span className="label-as-icon-wrapper label-feature">
          <IconWithTooltip
            iconProps={{
              name: "newInbox/label",
              gray: !showLabelAs,
              blue: showLabelAs,
              onClick: handleOpenLabelAs,
              large: true,
            }}
            tooltipProps={{ tooltipTextKey: "label-as" }}
          />
          <div className="custom-popup-wrapper">
            <CustomPopup showState={showLabelAs}>
              <LabelAs
                onClose={setShowLabelAs}
                selectedMails={getSelectedList()}
              />
            </CustomPopup>
          </div>
        </span>
      </div>
    </>
  );
};

const SaveIconButton = ({
  saveLoading,
  handleSaveRequest,
  getActiveMailsList,
}) => (
  <Tooltip
    id={"multipleSaveAction"}
    tooltipTextKey={"multiple-save-action-tooltip-text"}
    bottom
  >
    {saveLoading ? (
      <LoadingImageElement />
    ) : (
      <Icon
        name={`${getActiveMailsList().length > 0 ? "" : "un"}save-solid`}
        gray
        onClick={handleSaveRequest}
      />
    )}
  </Tooltip>
);

const SelectOptionsComponent = ({ currentSelect, setCurrentSelect }) => {
  const { getMailsData, setSelectedList } = useInbox();
  const { getTextByKey } = useContent();
  const mailsData = Object.keys(getMailsData() || {});

  const enumObj = {
    all: () => {
      return mailsData;
    },
    pendingDownload: () => {
      const selectList = mailsData.filter(
        (mailId) => getMailsData()[mailId].downloadsQuantity === 0
      );
      return selectList;
    },
    downloaded: () => {
      const selectList = mailsData.filter(
        (mailId) => getMailsData()[mailId].downloadsQuantity > 0
      );
      return selectList;
    },
    aboutToExpire: () => {
      const currentTime = new Date().getTime();
      const selectList = mailsData.filter((mailId) => {
        if (!getMailsData()[mailId].expirationDate) return false;
        const expireTime = new Date(
          getMailsData()[mailId].expirationDate
        ).getTime();
        const expTime = (expireTime - currentTime) / 1000 / 60 / 60;
        return expTime > 0 && expTime < 48;
      });
      return selectList;
    },
    jumbopay: () => {
      const selectList = mailsData.filter(
        (mailId) => getMailsData()[mailId].payment
      );
      return selectList;
    },
    saved: () => {
      const selectList = mailsData.filter(
        (mailId) => getMailsData()[mailId].isSaved
      );
      return selectList;
    },
    secured: () => {
      const selectList = mailsData.filter(
        (mailId) => getMailsData()[mailId].security
      );
      return selectList;
    },
  };

  const selectOptionFunc = (val) => {
    const resultArray = enumObj[val]();
    setSelectedList(resultArray);
    setCurrentSelect(val);
  };

  return (
    <div className="select-options-list">
      {Object.keys(selectOptionsList).map((item) => {
        return enumObj[item]().length > 0 ? (
          <div
            className={`select-option${
              currentSelect === item ? " highlighted" : ""
            }`}
            onClick={() => selectOptionFunc(item)}
            key={item}
          >
            {getTextByKey(selectOptionsList[item])}
          </div>
        ) : null;
      })}
    </div>
  );
};

const DesktopSelectComponent = () => {
  const [showSelectList, setShowSelectList] = useState(false);
  const { getQueryParam } = useInboxQueryParams();
  const { getTextByKey } = useContent();

  const {
    getSelectedList,
    getMailsData,
    setSelectedList,
    getCurrentSelectFilter,
    setCurrentSelectFilter,
    getIsFetching,
  } = useInbox();

  const { getProjectsQuantity } = usePicker();

  const { isLoggedIn } = useUser();

  const { desktopCondition, tabletCondition } = useMediaQueries();
  const mailsData = Object.keys(getMailsData() || {});

  const selectRef = useRef();
  useOnClickOutside(selectRef, () => setShowSelectList(false));

  const selectAllClick = () => {
    if (getSelectedList()?.length > 0) {
      setSelectedList([]);
      setCurrentSelectFilter("");
    } else {
      setSelectedList(mailsData);
      setCurrentSelectFilter("all");
    }
  };

  return desktopCondition || tabletCondition ? (
    <div
      className="select-bar-container"
      style={{
        opacity:
          !getIsFetching() &&
          (mailsData?.length > 0 || !!getProjectsQuantity()?.totalActive) &&
          isLoggedIn()
            ? 1
            : 0,
      }}
    >
      {getQueryParam("t") !== "picker" && (
        <div className="selectbox-component">
          {
            <div className="checkbox-image" onClick={selectAllClick}>
              <Image
                name={`checkbox-${
                  getSelectedList()?.length > 0 ? "" : "un"
                }checked`}
                ext="svg"
              />
            </div>
          }
          <div
            className="select-list-icon"
            onClick={() => {
              setShowSelectList((prev) => !prev);
            }}
            ref={selectRef}
          >
            <Icon name="arrow-down-solid" gray />
            <CustomPopup showState={showSelectList} topPosition={30}>
              <SelectOptionsComponent
                currentSelect={getCurrentSelectFilter()}
                setCurrentSelect={setCurrentSelectFilter}
              />
            </CustomPopup>
          </div>
          {getSelectedList()?.length > 0 ? <SelectedComponentsActions /> : null}
        </div>
      )}
      {window.location.pathname.includes("manageStorage") ? (
        <span className="manage-storage-description">
          {getTextByKey("manage-storage-description")}
        </span>
      ) : (
        <>
          <Filters />
          <StatusFilterBar />
        </>
      )}
      {/* {getQueryParam("t") === "trash" && !getSelectedList().length && (
        <p className="trash-notification">
          {getTextByKey("trash-notification")}
        </p>
      )} */}
      <div className="flex-spacer" />
      <PaginationComponent />
    </div>
  ) : null;
};

export default DesktopSelectComponent;
